import type {ModalBackdropProps} from "@project/design-system-web";
import type {ReactNode} from "react";

import {createContext, use, useEffect, useState} from "react";

import {AppModal} from "./app-modal";

// ─── Context ──────────────────────────────────────────────────────────────────

interface EntityDetailModalContextValue {
  mode: "view" | "edit";
  setMode: (mode: "view" | "edit") => void;
}

const EntityDetailModalContext = createContext<EntityDetailModalContextValue | null>(null);

function useEntityDetailModalContext() {
  const ctx = use(EntityDetailModalContext);

  if (!ctx)
    throw new Error("EntityDetailModal compound components must be used within EntityDetailModal");

  return ctx;
}

// ─── Props ────────────────────────────────────────────────────────────────────

export interface EntityDetailModalProps {
  isOpen: ModalBackdropProps["isOpen"];
  onOpenChange: ModalBackdropProps["onOpenChange"];
  viewTitle: ReactNode;
  editTitle: ReactNode;
  defaultMode?: "view" | "edit";
  children: ReactNode;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * EntityDetailModal — A generic state-machine modal archetype for displaying
 * and editing an entity without closing/reopening the modal.
 *
 * Implements Option B UX: Successful edits should close the modal entirely.
 */
export function EntityDetailModal({
  isOpen,
  onOpenChange,
  viewTitle,
  editTitle,
  defaultMode = "view",
  children,
}: EntityDetailModalProps) {
  const [mode, setMode] = useState<"view" | "edit">(defaultMode);

  // Reset to default mode whenever the modal opens or closes
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;

    if (!isOpen) {
      // Small timeout to allow exit animation to complete before snapping state
      timer = setTimeout(() => setMode(defaultMode), 300);
    } else {
      setMode(defaultMode);
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [isOpen, defaultMode]);

  return (
    <EntityDetailModalContext value={{mode, setMode}}>
      <AppModal.Backdrop isOpen={isOpen} onOpenChange={onOpenChange}>
        <AppModal.Container>
          <AppModal.Dialog className="sm:max-w-md">
            <AppModal.CloseTrigger />
            <AppModal.Header>
              <AppModal.Heading>{mode === "view" ? viewTitle : editTitle}</AppModal.Heading>
            </AppModal.Header>
            {children}
          </AppModal.Dialog>
        </AppModal.Container>
      </AppModal.Backdrop>
    </EntityDetailModalContext>
  );
}

EntityDetailModal.View = function EntityDetailModalView({children}: {children: ReactNode}) {
  const {mode} = useEntityDetailModalContext();

  if (mode !== "view") return null;

  return <>{children}</>;
};

EntityDetailModal.Edit = function EntityDetailModalEdit({children}: {children: ReactNode}) {
  const {mode} = useEntityDetailModalContext();

  if (mode !== "edit") return null;

  return <>{children}</>;
};

/**
 * A utility hook allowing child components (like the View's "Edit" button
 * or the Edit Form's "Cancel" button) to safely transition the modal's state.
 */
export function useEntityDetailModalState() {
  return useEntityDetailModalContext();
}
